import {
  CreateCheckoutSessionParams,
  CreateCheckoutSessionResult,
  CreateCustomOneTimePaymentCheckoutSessionArgs,
} from "@/backend/base";
import { displayUiMessage } from "@/components/utils/display-message";
import { editorContextStore } from "@/contexts/editor-context";
import {
  CheckoutSessionCallbackURL,
  getCheckoutSessionCallbackURLs,
} from "@/core/common/types/checkout-session-state";
import { QuotaOneTimePayment } from "@/core/common/types/quota-one-time-payment";
import { CreateStripePortalLinkParams } from "@/core/common/types/stripe-portal";
import { StripeSubscriptionPriceId } from "@/core/common/types/subscription";
import { debugError } from "@/core/utils/print-utilts";

export async function subscribeToPlanInternal({
  openNewPage = false,
  ...props
}: CreateCheckoutSessionParams & { openNewPage?: boolean }): Promise<CreateCheckoutSessionResult> {
  const newWindow = openNewPage && window.open("", "_blank");

  const backend = editorContextStore.getState().backend;
  if (!backend) {
    return {};
  }

  const { cancelUrl: defaultCancelURL, successUrl: defaultSuccessURL } =
    getCheckoutSessionCallbackURLs();

  return await backend
    ?.createCheckoutSession({
      ...props,
      cancel_url: props.cancel_url || defaultCancelURL,
      success_url: props.success_url || defaultSuccessURL,
    })
    .then((response) => {
      const { url, message } = response;

      if (url) {
        if (newWindow) {
          newWindow.location.assign(url);
        } else {
          window.location.assign(url);
        }
      } else {
        if (message) {
          displayUiMessage(message);
        }
      }

      return response;
    })
    .catch((error) => {
      debugError(error);

      displayUiMessage("Unknown error occurred. Please refresh the page.", "error");

      return {};
    });
}

export async function subscribeToPlan(price: StripeSubscriptionPriceId, openNewPage = false) {
  const { successUrl, cancelUrl } = getCheckoutSessionCallbackURLs();

  return subscribeToPlanInternal({
    openNewPage,
    line_items: [{ price, quantity: 1 }],
    success_url: successUrl,
    cancel_url: cancelUrl,
  });
}

export async function subscribeToApiStandardPlan() {
  // const apiUrl = appendPathToRootUrl("api");

  const { successUrl, cancelUrl } = getCheckoutSessionCallbackURLs();

  return await subscribeToPlanInternal({
    line_items: [{ price: StripeSubscriptionPriceId.ApiStandardPlan }],
    cancel_url: cancelUrl,
    success_url: successUrl,
  });
}

export interface SubscribeToOneTimePaymentArgs extends QuotaOneTimePayment {
  quantity: number;
  cancelUrl?: CheckoutSessionCallbackURL;
  successUrl?: CheckoutSessionCallbackURL;
  openNewPage?: boolean;
}

export async function subscribeToOneTimePayment({
  numCustomModelTrainings = 0,
  numCustomModelPredictions = 0,
  numVideoGenerations = 0,
  numFreeApiCredits = 0,
  quantity,
  cancelUrl,
  successUrl,
  openNewPage = false,
}: SubscribeToOneTimePaymentArgs) {
  try {
    const backend = editorContextStore.getState()?.backend;

    if (!backend) {
      return;
    }

    const { cancelUrl: defaultCancelURL, successUrl: defaultSuccessURL } =
      getCheckoutSessionCallbackURLs();
    cancelUrl = cancelUrl || defaultCancelURL;
    successUrl = successUrl || defaultSuccessURL;

    const newWindow = openNewPage && window.open("", "_blank");

    const response = await backend.createOneTimePaymentCheckoutSession({
      priceId: StripeSubscriptionPriceId.OneTimePayment,
      quantity,
      metadata: {
        numCustomModelTrainings,
        numCustomModelPredictions,
        numVideoGenerations,
        numFreeApiCredits,
      },
      successUrl,
      cancelUrl,
    });

    if (!response.ok) {
      displayUiMessage(
        // @ts-expect-error
        `Error creating payment portal: ${response.message}`,
        "error",
      );
      return;
    }

    const url = response.url;

    if (newWindow) {
      newWindow.location.assign(url);
    } else {
      window.location.assign(url);
    }
  } catch (error) {
    debugError("Error creating one-time payment: ", error);

    displayUiMessage("Error creating one-time payment. Please try again.", "error");
  }
}

export interface SubscribeToCustomOneTimePaymentArgs
  extends CreateCustomOneTimePaymentCheckoutSessionArgs {
  openNewPage?: boolean;
}

export async function subscribeToCustomOneTimePayment({
  openNewPage = false,
  ...args
}: SubscribeToCustomOneTimePaymentArgs) {
  try {
    const backend = editorContextStore.getState()?.backend;

    if (!backend) {
      return;
    }

    const newWindow = openNewPage && window.open("", "_blank");

    const response = await backend.createCustomOneTimePaymentCheckoutSession(args);

    if (!response.ok) {
      displayUiMessage(
        // @ts-expect-error
        `Error creating payment portal: ${response.message}`,
        "error",
      );
      return;
    }
    const url = response.url;
    if (newWindow) {
      newWindow.location.assign(url);
    } else {
      window.location.assign(url);
    }
  } catch (error) {
    debugError("Error creating custom one-time payment: ", error);

    displayUiMessage("Error creating custom one-time payment. Please try again.", "error");
  }
}

export async function openSubscriptionsLink(openNewPage = true) {
  return await editorContextStore
    .getState()
    .backend?.createSubscriptionsPortalLink({ returnUrl: window.location.origin })
    .then((url) => {
      if (!url) {
        console.error("Url is invalid");
        return;
      }
      const newWindow = openNewPage && window.open("", "_blank");
      if (newWindow) {
        newWindow.location.assign(url);
      } else {
        window.location.assign(url);
      }
    })
    .catch((error) => {
      console.error(error);

      displayUiMessage("Unknown error occurred. Please refresh the page.", "error");
    });
}

interface OpenStripePortalLinkInternalResponse {
  ok: boolean;
  message: string;
}

async function openStripePortalLinkInternal({
  openNewPage = true,
  ...params
}: CreateStripePortalLinkParams & {
  openNewPage?: boolean;
} = {}): Promise<OpenStripePortalLinkInternalResponse> {
  try {
    const { backend } = editorContextStore.getState();

    if (!backend) {
      return {
        ok: false,
        message: "Backend not initialized. Please refresh the page.",
      };
    }

    const { url } = await backend.createStripePortalLink({
      return_url: window.location.origin,
      ...params,
    });

    if (!url) {
      return {
        ok: false,
        message: "Cannot open payment portal. Please refresh the page.",
      };
    }

    const newWindow = openNewPage && window.open("", "_blank");

    if (newWindow) {
      newWindow.location.assign(url);
    } else {
      window.location.assign(url);
    }

    return {
      ok: true,
      message: "OK",
    };
  } catch (error) {
    console.log(`Error opening payment portal: ${String(error)}`, "error");
    return {
      ok: false,
      message: "Cannot open payment portal",
    };
  }
}

export async function openStripePortalLink(
  params: CreateStripePortalLinkParams & { openNewPage?: boolean } = {},
) {
  try {
    const openLinkResponse = await openStripePortalLinkInternal(params);

    if (!openLinkResponse.ok) {
      const openDefaultResponse = await openStripePortalLinkInternal();

      if (!openDefaultResponse.ok) {
        displayUiMessage(openDefaultResponse.message, "error");
      }
    }
  } catch (error) {
    displayUiMessage(`Error opening payment portal: ${String(error)}`, "error");
  }
}
